import components from './data/components.json';
import external from './data/external.json';
import pages from './data/pages.json';
import coreFlow from './data/core-flow.json';
import voucherFlow from './data/voucher-flow.json';
import {
  Component,
  ExternalSystem,
  Flow,
  JourneyNode,
  JourneyStep,
  Lane,
  Page,
  Ref,
} from './types';

const registry = components as unknown as Record<string, Component>;
const externals = external as unknown as Record<string, ExternalSystem>;
const pageRegistry = pages as unknown as Record<string, Page>;

export const JOURNEYS: { id: string; label: string; flow: Flow }[] = [
  { id: 'core', label: 'Core ticket purchase', flow: coreFlow as Flow },
  { id: 'voucher', label: 'Gift vouchers', flow: voucherFlow as Flow },
];

/** The notes carry a little HTML (<b>, <code>); drop the tags rather than render them. */
export const stripTags = (text: string) =>
  text
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** The registry badges the storyboard marks on a card. */
const STATUS_BY_BADGE: Record<string, JourneyNode['status']> = {
  legacy: 'legacy',
  'not-live': 'not-live',
};

/** `{domain}` stands for the flow's own domain wherever a component names a host. */
const withDomain = (text: string | undefined, domain?: string) =>
  text && domain ? text.replace(/\{domain\}/g, domain) : text;

const toNode = (
  { ref, desc }: Ref,
  domain?: string,
): JourneyNode | undefined => {
  const component = registry[ref];
  const system = component ? undefined : externals[ref];
  const found = component ?? system;
  if (!found) return undefined;
  const repo = component?.repo?.split('/')[1];
  const tech =
    component?.tech ??
    [system?.protocol, system?.host].filter(Boolean).join(' · ');
  return {
    id: ref,
    name: found.name,
    team: (
      component?.owningTeam ??
      system?.owningTeam ??
      'External'
    ).toLowerCase(),
    tech: withDomain(tech || undefined, domain),
    desc: desc ?? found.desc,
    entityName: repo,
    status: STATUS_BY_BADGE[component?.badge ?? ''],
  };
};

const nodes = (refs: Ref[] = [], domain?: string, pageId?: string) =>
  refs
    .filter(r => !r.pages || (pageId !== undefined && r.pages.includes(pageId)))
    .map(r => toNode(r, domain))
    .filter((node): node is JourneyNode => node !== undefined);

/** The edge components every step passes through: those that name no pages. */
export const resolveEdge = (flow: Flow): JourneyNode[] =>
  nodes(
    (flow.edge ?? []).filter(r => !r.pages),
    flow.domain,
  );

/** Steps run in the flow's order; edge components apply only to the pages they name. */
export const resolveSteps = (flow: Flow): JourneyStep[] =>
  flow.pages.flatMap((id, index) => {
    const page = pageRegistry[id];
    if (!page) return [];
    const lanes: Record<Lane, JourneyNode[]> = {
      edge: nodes(
        (flow.edge ?? []).filter(r => r.pages),
        flow.domain,
        id,
      ),
      ui: nodes(page.ui, flow.domain),
      services: nodes(page.services, flow.domain),
      adaptors: nodes(page.adaptors, flow.domain),
    };
    const notes = [page.bypassFlag, ...(page.handoffs ?? [])]
      .filter((note): note is string => Boolean(note))
      .map(stripTags);
    return [
      {
        id,
        number: index + 1,
        title: page.title,
        quote: page.quote,
        lanes,
        isAsync: /event-driven|reversed/i.test(page.bypassFlag ?? ''),
        notes,
        isLive: ![...lanes.ui, ...lanes.services, ...lanes.adaptors].some(
          node => node.status === 'not-live',
        ),
      },
    ];
  });

export const resolveExternals = (
  flow: Flow,
): (ExternalSystem & { id: string })[] =>
  (flow.externalStrip?.systems ?? []).flatMap(({ ref }) =>
    externals[ref] ? [{ id: ref, ...externals[ref] }] : [],
  );
